import { PathValue, UseFormReturn, useWatch } from 'react-hook-form';
import { PropertyValidationInformation } from '../../../util/PropertyValidationInformation';
import { AutocompleteTextFieldProps } from '../../../../../../react-commons/components/form/inputs/Form_ReferenceInput';
import {
    useIsDialogOpened,
    useNestedDialogFunction,
} from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import {
    ProcessAreaCreationDialog,
} from '../../../page-components/project/reusable-elements/process-area/ProcessAreaCreationDialog';
import React from 'react';
import { Form_AutocompleteReferenceTextField } from './Form_AutocompleteReferenceTextField';
import {
    ProcessAreaEditDialog,
} from '../../../page-components/project/reusable-elements/process-area/ProcessAreaEditDialog';
import { useMyNavigate } from '../../../router/createdRouter';
import { useProcessAreaActions } from '../../../../resource/resources/artifacts/process_area/processAreaResource';
import { useSystemInformationActions } from '../../../../resource/services/systemInformationService';

export const Form_ProcessAreaTextField = <T extends object, N extends boolean>({
                                                                                   projectVersionId,
                                                                                   projectId,
                                                                                   nullable,
                                                                                   name,
                                                                                   form,
                                                                                   propertyValidationInformation,
                                                                                   readOnly,
                                                                               }: {
    projectId: string | undefined | null;
    projectVersionId: string | undefined | null;
    form: UseFormReturn<T>;
    propertyValidationInformation: PropertyValidationInformation;
    readOnly: boolean;
} & AutocompleteTextFieldProps<T, N>) => {
    const objectId = useWatch({ control: form.control, name });

    const navigate = useMyNavigate();

    const nestedDialog = useNestedDialogFunction();
    const isNestedDialogOpen = useIsDialogOpened();

    const processAreaActions = useProcessAreaActions();
    const systemInformationActions = useSystemInformationActions();
    const processAreasQuery = processAreaActions.useGetAllResources(projectVersionId);
    const propertyDetails = systemInformationActions.useGetPropertyDetails(
        propertyValidationInformation.propertyIdentifier,
        propertyValidationInformation.resourceType,
    );
    const propertyValidation = systemInformationActions.useGetPropertyValidationWithId(
        propertyValidationInformation.propertyIdentifier,
        propertyValidationInformation.resourceType,
        propertyValidationInformation.resourceId ?? '',
    );

    const onGoToClick = (resourceId: string | undefined, _openInNewTab: boolean) => {
        const url = `/project/${projectId}/active-version/${projectVersionId}/process-area/${resourceId}`;
        if (isNestedDialogOpen) {
            window.open(url, `processArea_${resourceId}`);
        } else {
            navigate({
                route: '/project/:id/active-version/:versionId/process-area/:processAreaId',
                routeParams: {
                    id: projectId ?? '',
                    versionId: projectVersionId ?? '',
                    processAreaId: resourceId ?? '',
                },
                searchParams: {},
            });
        }
    };

    const handleChange = (id: string | null) => {
        form.setValue(name, id as PathValue<T, typeof name>, { shouldDirty: true, shouldValidate: true });
    };

    const onEdit = () => {
        nestedDialog.showNestedDialog(
            <ProcessAreaEditDialog
                onClose={nestedDialog.closeNestedDialog}
                objectId={objectId}
                versionedInId={projectVersionId ?? ''}
            />,
        );
    };

    const onAdd = () => {
        nestedDialog.showNestedDialog(
            <ProcessAreaCreationDialog
                onClose={nestedDialog.closeNestedDialog}
                parentId={projectVersionId ?? ''}
                onCreated={(_, newObjectId) => {
                    if (handleChange) {
                        handleChange(newObjectId);
                    }
                }}
            />,
        );
    };

    return (
        <Form_AutocompleteReferenceTextField
            control={form.control}
            idSelector={"objectId"}
            nullable={nullable}
            name={name}
            readOnly={readOnly}
            onGoToClick={onGoToClick}
            propertyValidation={propertyValidation}
            propertyDetails={propertyDetails}
            label={'Process Area'}
            query={processAreasQuery}
            labelAccessor={'name'}
            onChange={_ => form.trigger()}
            onAdd={onAdd}
            onEdit={onEdit}
        />
    );
};